import { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { FileText, Image } from 'lucide-react';
import { getRequest } from '../services/apiService';

export default function SCurveChart({ selectedProject, selectedShip }) {
  const chartRef = useRef(null);

  // State for fetched S-Curve data
  const [sCurveData, setSCurveData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableYears, setAvailableYears] = useState(['All']);
  const [selectedYear, setSelectedYear] = useState('2023'); // Changed default to '2023'
  useEffect(() => {
    const fetchSCurveData = async () => {
      if (!selectedShip) {
        setSCurveData(null);
        setFilteredData([]);
        setAvailableYears(['All']);
        setSelectedYear('2023'); // Ensure this also resets to 2023 if no ship is selected
        return;
      }

      setLoading(true);
      try {
        const endpoint = `/transaction/api/project/${selectedShip}/s-curve/`;
        const data = await getRequest(endpoint);
        setSCurveData(data);

        const yearsFromData = new Set();
        if (data?.["Contractual S-Curve"]?.Summary) {
          Object.keys(data["Contractual S-Curve"].Summary).forEach(key => {
            yearsFromData.add(key.substring(0, 4));
          });
        }
        if (data?.["Revised S-Curve"]?.Summary) {
          Object.keys(data["Revised S-Curve"].Summary).forEach(key => {
            yearsFromData.add(key.substring(0, 4));
          });
        }
        // Corrected key to access Achieved data based on your API response
        if (data?.["Achieved Progress %"]?.Summary) {
          Object.keys(data["Achieved Progress %"].Summary).forEach(key => {
            yearsFromData.add(key.substring(0, 4));
          });
        }

        const sortedYears = Array.from(yearsFromData).sort();
        setAvailableYears(['All', ...sortedYears]);

        // If the current selectedYear is no longer in availableYears, reset it to 'All'
        // You might want to adjust this logic if '2023' is not always available
        if (selectedYear !== 'All' && !sortedYears.includes(selectedYear) && selectedYear !== '2023') {
          setSelectedYear('All');
        } else if (!sortedYears.includes('2023') && selectedYear === '2023') { // Added condition to handle if 2023 isn't available
             setSelectedYear('All');
        }


      } catch (error) {
        console.error("Error fetching S-Curve data:", error);
        setSCurveData(null);
        setAvailableYears(['All']);
        setSelectedYear('2023'); // Reset selected year on error to 2023
      } finally {
        setLoading(false);
      }
    };

    fetchSCurveData();
  }, [selectedShip]); // Only re-fetch when selectedShip changes

  const [filteredData, setFilteredData] = useState([]);

  useEffect(() => {
    if (!sCurveData) {
      setFilteredData([]);
      return;
    }

    const contractualSummary = sCurveData?.["Contractual S-Curve"]?.Summary || {};
    const revisedSummary = sCurveData?.["Revised S-Curve"]?.Summary || {};
    // Corrected key for achievedSummary
    const achievedSummary = sCurveData?.["Achieved Progress %"]?.Summary || {};

    const combinedData = {};

    const allKeys = new Set([
      ...Object.keys(contractualSummary),
      ...Object.keys(revisedSummary),
      ...Object.keys(achievedSummary)
    ]);

    Array.from(allKeys).sort((a,b) => {
        // Sort "YYYY-MM" strings chronologically
        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
    }).forEach(key => {
      const [year, monthNum] = key.split('-');
      const monthName = new Date(year, monthNum - 1, 1).toLocaleString('en-US', { month: 'short' });

      if (selectedYear === 'All' || year === selectedYear) {
        combinedData[key] = {
          month: monthName,
          year: parseInt(year),
          fullMonthYear: key, // New: "YYYY-MM" string for dataKey
          displayLabel: `${monthName}-${year.substring(2)}`, // Retained for tooltip/reference if needed
          Contractual: contractualSummary[key] !== undefined ? parseFloat(contractualSummary[key]) : null,
          Revised: revisedSummary[key] !== undefined ? parseFloat(revisedSummary[key]) : null,
          Achieved: achievedSummary[key] !== undefined ? parseFloat(achievedSummary[key]) : null,
        };
      }
    });

    const currentFilteredData = Object.values(combinedData).sort((a, b) => {
        // Sort by year, then by month (using month number for correct order)
        if (a.year !== b.year) return a.year - b.year;
        const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return monthOrder.indexOf(a.month) - monthOrder.indexOf(b.month);
    });

    setFilteredData(currentFilteredData);

    // Console log the Achieved data for the selected year
    const achievedDataForSelectedYear = currentFilteredData.map(item => ({
      month: item.month,
      year: item.year,
      achieved: item.Achieved
    }));
    // console.log(`Achieved Data for Year ${selectedYear}:`, achievedDataForSelectedYear);

  }, [sCurveData, selectedYear]); // Re-filter when sCurveData or selectedYear changes

  // Download CSV function
  const downloadCSV = () => {
    if (filteredData.length === 0) {
      alert('No data available to download');
      return;
    }

    const headers = ['Month', 'Year', 'Contractual (%)', 'Revised (%)', 'Achieved (%)'];
    const csvContent = [
      headers.join(','),
      ...filteredData.map(row => [
        row.month,
        row.year,
        row.Contractual !== null ? row.Contractual : '',
        row.Revised !== null ? row.Revised : '',
        row.Achieved !== null ? row.Achieved : ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `scurve-data-${selectedProject}-${selectedShip}-${selectedYear}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download PNG function
  const downloadPNG = () => {
    const chartContainer = chartRef.current;
    if (!chartContainer) {
      alert('Chart not found');
      return;
    }

    const svgElement = chartContainer.querySelector('svg');
    if (!svgElement) {
      alert('Chart SVG not found');
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const rect = svgElement.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;

      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          const link = document.createElement('a');
          link.download = `scurve-chart-${selectedProject}-${selectedShip}-${selectedYear}.png`;
          link.href = URL.createObjectURL(blob);
          link.click();

          URL.revokeObjectURL(svgUrl);
          URL.revokeObjectURL(link.href);
        }, 'image/png');
      };

      img.onerror = () => {
        alert('Error generating chart image');
        URL.revokeObjectURL(svgUrl);
      };

      img.src = svgUrl;
    } catch (error) {
      console.error('Error downloading PNG:', error);
      alert('Error generating chart image');
    }
  };

  // Custom tooltip style
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0]?.payload;
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
          <p className="font-bold text-gray-800">{dataPoint.month} {dataPoint.year}</p>
          <div className="mt-2">
            {payload.map((entry, index) => (
              <div key={`item-${index}`} className="flex items-center mb-1">
                <div
                  className="w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm mr-2">{entry.name}:</span>
                <span className="text-sm font-semibold">{entry.value !== null ? `${entry.value}%` : 'N/A'}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Format ticks for X-axis based on the filter selection
  const formatXAxisTick = (value) => {
    if (selectedYear === 'All') {
      // value will be like "2023-01", "2023-02", etc.
      const [year, monthNum] = value.split('-');
      if (monthNum === '01') { // Only display year for January (first month of the year)
        return year;
      }
      return ''; // Hide for other months to prevent clutter
    }
    return value; // For specific year, value is already the month name (e.g., "Jan")
  };

  return (
    <div className="flex flex-col items-center w-full p-6 rounded-xl">

      <div className="w-full flex flex-wrap mb-8 gap-4 justify-end items-end">
        {/* Year Selector (managed internally by SCurveChart) */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Select Year:</label>
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(e.target.value)}
            className="text-sm border rounded-lg px-3 py-1 bg-white"
            disabled={loading || availableYears.length <= 1}
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {/* Download buttons */}
        <div className="flex gap-3">
          <button
            onClick={downloadCSV}
            disabled={filteredData.length === 0}
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-1 text-sm shadow-sm"
            title="Download CSV"
          >
            <FileText size={16} className="mr-1" />
            CSV
          </button>
          <button
            onClick={downloadPNG}
            disabled={filteredData.length === 0}
            className="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center space-x-1 text-sm shadow-sm"
            title="Download PNG"
          >
            <Image size={16} className="mr-1" />
            PNG
          </button>
        </div>
      </div>

      <div ref={chartRef} className="w-full h-96 rounded-xl bg-white shadow-xl p-6 border border-gray-100">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-gray-500">Loading S-Curve data...</p>
          </div>
        ) : filteredData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={filteredData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 10,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey={selectedYear === 'All' ? "fullMonthYear" : "month"} // Updated dataKey
                tick={{ fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
                tickLine={{ stroke: '#E5E7EB' }}
                tickFormatter={formatXAxisTick} // Updated tickFormatter logic
                interval={selectedYear === 'All' ? 'preserveStartEnd' : 'preserveStartEnd'}
                angle={selectedYear === 'All' ? -45 : 0}
                textAnchor={selectedYear === 'All' ? "end" : "middle"}
                height={50}
              />
              <YAxis
                label={{
                  value: 'Completion (%)',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: '#6B7280', textAnchor: 'middle' }
                }}
                tick={{ fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
                tickLine={{ stroke: '#E5E7EB' }}
                domain={[0, 100]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ paddingTop: '10px' }}
                iconType="circle"
              />
              <ReferenceLine y={100} stroke="#E5E7EB" strokeDasharray="3 3" />
              <Line
                type="monotone"
                dataKey="Contractual"
                name="Contractual"
                stroke="#4F46E5"
                strokeWidth={3}
                dot={{ stroke: '#4F46E5', strokeWidth: 2, r: 4, fill: 'white' }}
                activeDot={{ r: 6, stroke: '#4F46E5', strokeWidth: 2, fill: '#4F46E5' }}
              />
              <Line
                type="monotone"
                dataKey="Revised"
                name="Revised"
                stroke="#F59E0B"
                strokeWidth={3}
                dot={{ stroke: '#F59E0B', strokeWidth: 2, r: 4, fill: 'white' }}
                activeDot={{ r: 6, stroke: '#F59E0B', strokeWidth: 2, fill: '#F59E0B' }}
              />
              <Line
                type="monotone"
                dataKey="Achieved"
                name="Achieved"
                stroke="#10B981"
                strokeWidth={3}
                dot={{ stroke: '#10B981', strokeWidth: 2, r: 4, fill: 'white' }}
                activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2, fill: '#10B981' }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center">
            <p className="text-gray-500">No data available for the selected filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}